import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HiOfficeBuilding,
  HiDatabase,
  HiClipboardList,
  HiCurrencyDollar,
  HiDocumentText,
  HiShieldCheck,
  HiLogout,
  HiChevronUp,
  HiChevronDown,
} from '../icons';
import { XTNDLogo } from '../icons';
import { authService } from '../../services/auth';
import { showToast } from '../../utils/toast';
import { useDarkMode } from '../../stores/themeStore';
import { TenantSelector } from './TenantSelector';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

interface SidebarProps {
  collapsed: boolean;
}

export function Sidebar({ collapsed }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const { isDark } = useDarkMode();

  const navSections: NavSection[] = [
    {
      title: 'Management',
      items: [
        { path: '/tenants', label: 'Tenant Management', icon: HiOfficeBuilding },
        { path: '/replication', label: 'Data Replication', icon: HiDatabase },
      ],
    },
    {
      title: 'Operations',
      items: [
        { path: '/reconciliation', label: 'Reconciliation', icon: HiClipboardList },
        { path: '/juniper-data', label: 'Juniper Data', icon: HiCurrencyDollar },
      ],
    },
    {
      title: 'Tools',
      items: [
        { path: '/templates', label: 'Template Studio', icon: HiDocumentText },
        { path: '/privileged-access', label: 'Privileged Access', icon: HiShieldCheck },
      ],
    },
  ];

  const handleLogout = async () => {
    try {
      await authService.signOut();
      showToast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch {
      showToast.error('Failed to log out');
    }
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 z-40 h-screen
        bg-gradient-to-b from-white to-xtnd-light/30 dark:from-xtnd-dark-900 dark:to-xtnd-dark-900
        shadow-xl border-r border-xtnd-dark-200 dark:border-xtnd-dark-700
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-20' : 'w-72'}
      `}
      role="navigation"
      aria-label="Sidebar"
    >
      <div className="flex flex-col h-full">
        {/* Header with Logo */}
        <div className="flex flex-col items-center justify-center px-6 pt-6 pb-2 mb-2">
          <Link to="/tenants" className="flex items-center">
            {collapsed ? (
              <img
                src={isDark ? '/xtnd-logo-square-dark.png' : '/xtnd-logo-square.png'}
                alt="XTND"
                className="h-10 w-auto"
              />
            ) : (
              <XTNDLogo className="h-8 w-auto" />
            )}
          </Link>
          {!collapsed && (
            <div className="mt-2 text-[10px] font-semibold text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-widest">
              Admin Portal
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-4 mb-4 h-px bg-gradient-to-r from-transparent via-xtnd-dark-200 dark:via-xtnd-dark-700 to-transparent" />

        {/* Tenant Selector */}
        <div className={`px-4 mb-4 ${collapsed ? 'flex justify-center' : ''}`}>
          {!collapsed && <TenantSelector />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-6">
          {navSections.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              {/* Section Title */}
              {!collapsed && section.title && (
                <div className="px-3 mb-3">
                  <h3 className="text-xs font-bold text-xtnd-dark-400 dark:text-xtnd-dark-300 uppercase tracking-wider">
                    {section.title}
                  </h3>
                </div>
              )}

              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map(item => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(item.path + '/');

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`
                        group relative flex items-center gap-3 px-4 py-3 rounded-lg
                        text-sm font-medium transition-all duration-200
                        ${
                          isActive
                            ? 'bg-xtnd-dark-800 text-white dark:bg-xtnd-blue-700 dark:text-white shadow-lg'
                            : 'text-xtnd-dark-600 dark:text-xtnd-dark-200 hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-700 hover:text-xtnd-dark-900 dark:hover:text-white'
                        }
                        ${collapsed ? 'justify-center px-0' : ''}
                      `}
                      title={collapsed ? item.label : undefined}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon
                        className="h-5 w-5 flex-shrink-0 transition-transform group-hover:scale-110"
                      />
                      {!collapsed && (
                        <>
                          <span className="flex-1">{item.label}</span>
                          {isActive && (
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white dark:bg-xtnd-blue-400 rounded-l-full" />
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className="p-4">
          {currentUser && (
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className={`
                  w-full p-3 rounded-xl
                  bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50
                  hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-600
                  transition-all duration-200
                  ${collapsed ? 'flex justify-center' : ''}
                `}
              >
                <div className={`flex items-center gap-3 ${collapsed ? 'flex-col' : ''}`}>
                  <div className="relative">
                    {currentUser.picture ? (
                      <img
                        src={currentUser.picture}
                        alt={currentUser.name}
                        className="h-10 w-10 rounded-full ring-2 ring-xtnd-blue/20"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-xtnd-dark-700 to-xtnd-dark-900 dark:from-xtnd-blue to-xtnd-blue-600 text-white flex items-center justify-center font-bold shadow-lg">
                        {(currentUser.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 rounded-full border-2 border-white dark:border-xtnd-dark-900" />
                  </div>
                  {!collapsed && (
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-bold text-xtnd-dark-800 dark:text-white truncate">
                        {currentUser.name}
                      </div>
                      <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 truncate">
                        {currentUser.email}
                      </div>
                    </div>
                  )}
                  {!collapsed && (
                    profileOpen ? (
                      <HiChevronUp className="h-4 w-4 text-xtnd-dark-400 dark:text-xtnd-dark-300" />
                    ) : (
                      <HiChevronDown className="h-4 w-4 text-xtnd-dark-400 dark:text-xtnd-dark-300" />
                    )
                  )}
                </div>
              </button>

              {/* Profile Dropdown */}
              {profileOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setProfileOpen(false)}
                    onKeyDown={e => e.key === 'Escape' && setProfileOpen(false)}
                    role="button"
                    tabIndex={0}
                    aria-label="Close profile menu"
                  />
                  <div className="absolute bottom-0 left-full ml-2 w-56 bg-white dark:bg-xtnd-dark-800 rounded-xl shadow-xl border border-xtnd-dark-200 dark:border-xtnd-dark-700 z-20 overflow-hidden">
                    <div className="p-3">
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                      >
                        <HiLogout className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
